import { useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  BackgroundElement,
  DesignConfig,
  FontFamily,
  LifestyleElement,
  ProductImageElement,
  ShadowSpec,
  SourceFields,
} from '../api/types';
import {
  FONT_FAMILIES,
  FONT_FAMILY_LABEL,
  getLifestyle,
  initialLifestyle,
  isCustomElement,
  resolveProductImageBox,
} from '../api/types';
import { productBoxPatch, resetBoxPatch } from '../lib/productBox';
import ColorPickerField from './ColorPickerField';
import { BackgroundEditor } from './BackgroundEditor';
import { LifestyleEditor } from './LifestyleEditor';

const DEFAULT_BACKGROUND: BackgroundElement = {
  type: 'image',
  effect: { enabled: false, style: 'radial', direction: 'vertical', intensity: 0.22 },
};

interface Props {
  config: DesignConfig;
  sourceFields: SourceFields;
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
  onPatchElement: (key: string, patch: Record<string, unknown>) => void;
  onEnableLifestyle: (lifestyle: LifestyleElement) => void;
  onDisableLifestyle: () => void;
  onPatchLifestyle: (patch: Partial<LifestyleElement>) => void;
  onAddCustomElement: (key: string, element: Record<string, unknown>) => void;
  onRemoveElement: (key: string) => void;
  onUploadAsset: (kind: 'background' | 'logo' | 'lifestyle', file: File) => Promise<void>;
  uploading: 'background' | 'logo' | 'lifestyle' | null;
  // Effective cutout mode when a design leaves product_image.cutout unset —
  // needed to know whether "Fit to content" is a live knob or a no-op.
  storeDefaultCutout: 'auto' | 'none';
  // The width the product composition renders at. In lifestyle mode the
  // product image lives in the product half and the compositor composes that
  // half at (product_w, 1080), so the box must be resolved AND written at that
  // width — resolving at 1080 would show a band twice the rendered width and
  // persist it on the first edit. Required, never defaulted: the Editor owns
  // the render state that decides it (`productSplit`).
  productCanvasW: number;
}

const BUILTIN_LIST: Array<{ key: keyof DesignConfig['elements']; label: string }> = [
  { key: 'product_image', label: 'Product image' },
  { key: 'rating', label: 'Rating badge' },
  { key: 'deal', label: 'Deal sticker' },
  { key: 'price_card', label: 'Price card' },
  { key: 'logo', label: 'Brand logo' },
  { key: 'disclaimer', label: 'Disclaimer text' },
];

// Configs saved before the product_image element existed have no such key.
// An empty element resolves to the compositor's default band, so the first
// drag is a no-op relative to what the operator already sees rendered.
const PRODUCT_IMAGE_FALLBACK: Record<string, unknown> = { scale: 1.0 };

export default function ElementPanel({
  config,
  sourceFields,
  selectedKey,
  onSelect,
  onPatchElement,
  onEnableLifestyle,
  onDisableLifestyle,
  onPatchLifestyle,
  onAddCustomElement,
  onRemoveElement,
  onUploadAsset,
  uploading,
  storeDefaultCutout,
  productCanvasW,
}: Props) {
  const [adding, setAdding] = useState(false);

  const customKeys = Object.keys(config.elements).filter((k) =>
    isCustomElement((config.elements as unknown as Record<string, unknown>)[k]),
  );

  return (
    <aside className="space-y-6">
      <header>
        <p className="font-mono text-[11px] uppercase tracking-widish text-ff-mute">
          Composition
        </p>
        <h2 className="font-display text-2xl font-bold text-ff-ink">Elements</h2>
      </header>

      <AssetsBlock config={config} onUpload={onUploadAsset} uploading={uploading} />

      <section>
        <p className="font-mono text-[11px] uppercase tracking-widish text-ff-mute mb-3">
          Background
        </p>
        <BackgroundEditor
          value={config.elements.background ?? DEFAULT_BACKGROUND}
          onChange={(next) =>
            onPatchElement('background', next as unknown as Record<string, unknown>)
          }
        />
      </section>

      <div className="ff-divider-rule" />

      <section>
        <p className="font-mono text-[11px] uppercase tracking-widish text-ff-mute mb-3">
          Lifestyle
        </p>
        <LifestyleEditor
          value={getLifestyle(config.elements)}
          sourceFields={sourceFields}
          onEnable={() => onEnableLifestyle(initialLifestyle(sourceFields.custom))}
          onDisable={onDisableLifestyle}
          onChange={onPatchLifestyle}
          onUploadAsset={(file) => onUploadAsset('lifestyle', file)}
          uploading={uploading === 'lifestyle'}
        />
      </section>

      <div className="ff-divider-rule" />

      <div className="space-y-2">
        {BUILTIN_LIST.map(({ key, label }) => {
          const k = String(key);
          const raw = (config.elements as unknown as Record<string, Record<string, unknown>>)[k];
          const el = raw ?? (k === 'product_image' ? PRODUCT_IMAGE_FALLBACK : undefined);
          if (!el) return null;
          const isSel = selectedKey === k;
          return (
            <ElementCard
              key={k}
              elKey={k}
              label={label}
              element={el}
              selected={isSel}
              onSelect={() => onSelect(k)}
              onPatch={(patch) => onPatchElement(k, patch)}
              isCustom={false}
              onRemove={null}
              storeDefaultCutout={storeDefaultCutout}
              productCanvasW={productCanvasW}
            />
          );
        })}

        {customKeys.length > 0 && (
          <div className="pt-2">
            <p className="font-mono text-[10px] uppercase tracking-widish text-ff-mute mb-1.5">
              Custom overlays
            </p>
          </div>
        )}
        {customKeys.map((key) => {
          const el = config.elements[key] as unknown as Record<string, unknown>;
          const t = String(el.type ?? '');
          const labelText = String(el.value ?? '');
          const labelField = String(el.field ?? '');
          const label =
            t === 'text'
              ? `Text · "${labelText}"`
              : t === 'field'
                ? `Field · :${labelField}`
                : key;
          return (
            <ElementCard
              key={key}
              elKey={key}
              label={label}
              element={el}
              selected={selectedKey === key}
              onSelect={() => onSelect(key)}
              onPatch={(patch) => onPatchElement(key, patch)}
              isCustom={true}
              onRemove={() => onRemoveElement(key)}
              storeDefaultCutout={storeDefaultCutout}
              productCanvasW={productCanvasW}
            />
          );
        })}

        <button
          type="button"
          onClick={() => setAdding(true)}
          className="mt-2 w-full rounded-xl border border-dashed border-ff-line px-3 py-2.5 text-[11px] font-bold uppercase tracking-widish text-ff-mute transition-colors hover:border-ff-orange hover:text-ff-orange"
          data-testid="add-overlay-button"
        >
          + Add overlay
        </button>
      </div>

      {adding &&
        createPortal(
          <AddOverlayDialog
            existingKeys={Object.keys(config.elements)}
            sourceFields={sourceFields}
            onCancel={() => setAdding(false)}
            onCreate={(key, element) => {
              onAddCustomElement(key, element);
              onSelect(key);
              setAdding(false);
            }}
          />,
          document.body,
        )}
    </aside>
  );
}

function ElementCard({
  elKey,
  label,
  element,
  selected,
  onSelect,
  onPatch,
  isCustom,
  onRemove,
  storeDefaultCutout,
  productCanvasW,
}: {
  elKey: string;
  label: string;
  element: Record<string, unknown>;
  selected: boolean;
  onSelect: () => void;
  onPatch: (patch: Record<string, unknown>) => void;
  isCustom: boolean;
  onRemove: (() => void) | null;
  storeDefaultCutout: 'auto' | 'none';
  productCanvasW: number;
}) {
  const visible = element.visible !== false;
  // The product image is never hideable — an ad without its product is not a
  // design option, so the compositor ignores `visible` for this key.
  const hideable = elKey !== 'product_image';
  const pi = element as unknown as ProductImageElement;
  // The product image's box is resolved, not stored: a config carrying only
  // `scale` has no x/y, and the renderer centres it. Report that, not 0/0.
  // Folded into one `box` shape (w/h unused outside product_image) so the
  // X/Y grid below reads a single variable either way.
  const box =
    elKey === 'product_image'
      ? resolveProductImageBox(pi, productCanvasW)
      : { x: Number(element.x ?? 0), y: Number(element.y ?? 0), w: 0, h: 0 };
  // A design's own `cutout` always wins over the store default in both
  // directions (spec §1.1); "Fit to content" is a no-op once no-cutout mode
  // pastes the original photo, so disable it under the mode that will win.
  const effectiveNone = (pi.cutout ?? storeDefaultCutout) === 'none';
  // Local to the card: the (i) hint is a transient disclosure, not design state.
  const [showCutoutHint, setShowCutoutHint] = useState(false);

  return (
    <div
      className={`rounded-xl border transition-colors ${
        selected
          ? 'border-ff-orange bg-ff-accent-soft/20 shadow-soft'
          : 'border-ff-line bg-white hover:border-ff-ink/20'
      }`}
    >
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={onSelect}
          className="flex-1 flex items-center gap-3 min-w-0 px-4 py-3 text-left rounded-l-xl focus:outline-none focus:ring-2 focus:ring-ff-orange/30"
          data-testid={`element-card-${elKey}`}
          aria-pressed={selected}
        >
          <span
            className={`h-2.5 w-2.5 rounded-full shrink-0 ${
              visible ? (selected ? 'bg-ff-orange' : 'bg-ff-success') : 'bg-ff-line'
            }`}
          />
          <span className="font-medium text-ff-ink truncate">{label}</span>
        </button>
        {hideable && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onPatch({ visible: !visible });
            }}
            className="shrink-0 px-4 py-3 text-[10px] font-bold uppercase tracking-widish text-ff-mute hover:text-ff-ink rounded-r-xl focus:outline-none focus:ring-2 focus:ring-ff-orange/30"
            aria-label={visible ? 'Hide element' : 'Show element'}
          >
            {visible ? 'Visible' : 'Hidden'}
          </button>
        )}
      </div>

      {selected && (
        <div className="px-4 pb-4 pt-1 space-y-3 animate-rise">
          {elKey === 'product_image' ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <NumField label="X" value={box.x}
                          onChange={(v) => onPatch(productBoxPatch(pi, { x: v }, productCanvasW))} />
                <NumField label="Y" value={box.y}
                          onChange={(v) => onPatch(productBoxPatch(pi, { y: v }, productCanvasW))} />
                <NumField label="W" value={box.w} testId="element-product_image-w"
                          onChange={(v) => onPatch(productBoxPatch(pi, { w: Math.max(1, v) }, productCanvasW))} />
                <NumField label="H" value={box.h} testId="element-product_image-h"
                          onChange={(v) => onPatch(productBoxPatch(pi, { h: Math.max(1, v) }, productCanvasW))} />
              </div>
              <label className="flex items-center gap-2 text-sm text-ff-ink">
                <input type="checkbox" data-testid="element-product_image-trim"
                       checked={element.trim === true} disabled={effectiveNone}
                       onChange={(e) => onPatch({ trim: e.target.checked ? true : undefined })} />
                Fit to content
              </label>
              <p className="text-[11px] text-ff-mute">
                {effectiveNone
                  ? 'Always fitted to the photo in no-cutout mode.'
                  : 'Crop the cutout to its visible pixels before fitting, so the box governs apparent size.'}
              </p>
              <button type="button" className="ff-btn-ghost text-xs" data-testid="element-product_image-reset"
                      onClick={() => onPatch(resetBoxPatch())}>Reset box</button>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <NumField
                label="X"
                value={Number(box.x)}
                onChange={(v) => onPatch({ x: v, y: box.y })}
              />
              <NumField
                label="Y"
                value={Number(box.y)}
                onChange={(v) => onPatch({ x: box.x, y: v })}
              />
            </div>
          )}

          {hasFill(element) && (
            <ColorPickerField
              label="Fill"
              value={String(element.fill)}
              onChange={(c) => onPatch({ fill: c })}
            />
          )}
          {hasTextColor(element) && (
            <ColorPickerField
              label="Text"
              value={String(element.text_color)}
              onChange={(c) => onPatch({ text_color: c })}
            />
          )}
          {hasSaleColor(element) && (
            <ColorPickerField
              label="Main price"
              value={String(element.sale_color)}
              onChange={(c) => onPatch({ sale_color: c })}
            />
          )}
          {elKey === 'price_card' && (
            <>
              <ColorPickerField
                label="Was price"
                value={String(element.strike_color ?? DEFAULT_STRIKE_COLOR)}
                onChange={(c) => onPatch({ strike_color: c })}
              />
              <ColorPickerField
                label="Regular price"
                value={String(
                  element.regular_color ?? element.text_color ?? DEFAULT_REGULAR_COLOR,
                )}
                onChange={(c) => onPatch({ regular_color: c })}
              />
            </>
          )}
          {hasColor(element) && (
            <ColorPickerField
              label="Colour"
              value={String(element.color)}
              onChange={(c) => onPatch({ color: c })}
            />
          )}

          {hasScale(elKey, isCustom) && (
            <SliderField
              label="Scale"
              value={Number(element.scale ?? 1.0)}
              min={0.5}
              max={2.0}
              step={0.05}
              format={(v) => `${v.toFixed(2)}×`}
              testId={`element-${elKey}-scale`}
              onChange={(v) => onPatch({ scale: round2(v) })}
            />
          )}

          {elKey === 'product_image' && (
            <div>
              <div className="flex items-center justify-between">
                <label className="ff-label" htmlFor="element-product_image-cutout">Cutout</label>
                <button type="button" aria-label="About the store default" data-testid="cutout-default-info"
                        className="grid h-5 w-5 place-items-center rounded-full border border-ff-line text-[10px] font-bold text-ff-mute hover:text-ff-ink"
                        onClick={() => setShowCutoutHint((v) => !v)}>i</button>
              </div>
              <select
                id="element-product_image-cutout"
                value={(element.cutout as string) ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  // '' = follow store default: remove the key entirely so
                  // pre-existing configs stay byte-identical (spec §1.1).
                  onPatch(v === '' ? { cutout: undefined } : { cutout: v });
                }}
                className="ff-input text-sm"
                data-testid="element-product_image-cutout"
              >
                <option value="">
                  Store default ({storeDefaultCutout === 'none' ? 'No cutout' : 'Cutout pipeline'})
                </option>
                <option value="none">No cutout (original photo)</option>
                <option value="auto">Cutout pipeline</option>
              </select>
              {showCutoutHint && (
                <p role="note" data-testid="cutout-default-hint" className="mt-1.5 rounded-xl border border-ff-line bg-white px-3 py-2 text-[11px] text-ff-mute">
                  The store default is set in <strong>Store settings → Cutout mode</strong> (the gear next to
                  the store name). A design's explicit choice here always wins over the store default.
                </p>
              )}
            </div>
          )}

          {hasShape(elKey, isCustom) && (
            <ShapeSelector
              elKey={elKey}
              value={(element.shape as ShapeValue) ?? 'pill'}
              onChange={(s) => onPatch({ shape: s })}
            />
          )}

          {hasFontFamily(elKey, isCustom) && (
            <FontFamilySelector
              elKey={elKey}
              value={(element.font_family as FontFamily) ?? 'Inter'}
              onChange={(f) => onPatch({ font_family: f })}
            />
          )}

          {elKey === 'price_card' && (
            <>
              <GroupLabel>Main price</GroupLabel>
              <FontFamilySelector
                elKey="price_card-sale"
                value={
                  (element.sale_font_family as FontFamily) ??
                  (element.font_family as FontFamily) ??
                  'Inter'
                }
                onChange={(f) => onPatch({ sale_font_family: f })}
              />
              <SliderField
                label="Font size"
                value={Number(element.font_size ?? DEFAULT_SALE_FONT_SIZE)}
                min={24}
                max={160}
                step={2}
                format={(v) => `${Math.round(v)} px`}
                testId={`element-price_card-font-size`}
                onChange={(v) => onPatch({ font_size: Math.round(v) })}
              />

              <GroupLabel>Was price (strikethrough)</GroupLabel>
              <FontFamilySelector
                elKey="price_card-strike"
                value={
                  (element.strike_font_family as FontFamily) ??
                  (element.font_family as FontFamily) ??
                  'Inter'
                }
                onChange={(f) => onPatch({ strike_font_family: f })}
              />
              <SliderField
                label="Font size"
                value={Number(
                  element.strike_font_size ??
                    derivedStrikeSize(Number(element.font_size ?? DEFAULT_SALE_FONT_SIZE)),
                )}
                min={12}
                max={120}
                step={1}
                format={(v) => `${Math.round(v)} px`}
                testId="element-price_card-strike-font-size"
                onChange={(v) => onPatch({ strike_font_size: Math.round(v) })}
              />
            </>
          )}

          {elKey === 'disclaimer' && (
            <SliderField
              label="Font size"
              value={Number(element.font_size ?? element.size ?? 18)}
              min={10}
              max={48}
              step={1}
              format={(v) => `${Math.round(v)} px`}
              testId="element-disclaimer-font-size"
              onChange={(v) => onPatch({ font_size: Math.round(v), size: Math.round(v) })}
            />
          )}

          {isCustom && (
            <SliderField
              label="Font size"
              value={Number(element.font_size ?? 28)}
              min={12}
              max={120}
              step={1}
              format={(v) => `${Math.round(v)} px`}
              testId={`element-${elKey}-font-size`}
              onChange={(v) => onPatch({ font_size: Math.round(v) })}
            />
          )}

          {isCustom && element.type === 'text' && (
            <div>
              <label className="ff-label">Text</label>
              <input
                type="text"
                value={String(element.value ?? '')}
                onChange={(e) => onPatch({ value: e.target.value })}
                className="ff-input font-mono text-sm"
                data-testid={`element-${elKey}-value`}
              />
            </div>
          )}

          {hasShadow(elKey, isCustom) && (
            <ShadowControl
              elKey={elKey}
              shadow={(element.shadow ?? null) as ShadowSpec | null}
              onChange={(s) => onPatch({ shadow: s })}
            />
          )}

          {hasBorderRadius(elKey, isCustom) && (
            <SliderField
              label="Corner radius"
              value={Number(element.border_radius ?? 0)}
              min={0}
              max={120}
              step={2}
              format={(v) => `${Math.round(v)} px`}
              testId={`element-${elKey}-border-radius`}
              onChange={(v) => onPatch({ border_radius: Math.round(v) })}
            />
          )}

          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="w-full mt-2 rounded-xl border border-ff-danger/30 bg-ff-danger-soft px-3 py-2 text-[11px] font-bold uppercase tracking-widish text-ff-danger hover:bg-ff-danger-soft/70 transition-colors"
              data-testid={`element-${elKey}-remove`}
            >
              Remove overlay
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function round2(n: number) {
  return Math.round(n * 100) / 100;
}

// Mirrors feedforge/layout.py: PRICE_STRIKE_TEXT, PRICE_REGULAR_TEXT and the
// 1080-space defaults of PRICE_SALE_FONT_RATIO / PRICE_STRIKE_FONT_RATIO.
const DEFAULT_STRIKE_COLOR = '#5A5A5A';
const DEFAULT_REGULAR_COLOR = '#141620';
const DEFAULT_SALE_FONT_SIZE = 77;
const STRIKE_TO_SALE_RATIO = 0.034 / 0.072;

/** The strike size the compositor derives when strike_font_size is unset. */
function derivedStrikeSize(saleFontSize: number) {
  return Math.round(saleFontSize * STRIKE_TO_SALE_RATIO);
}

function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="pt-1 font-mono text-[10px] uppercase tracking-widish text-ff-mute">
      {children}
    </p>
  );
}

function hasScale(key: string, isCustom: boolean) {
  if (isCustom) return false;
  // product_image dropped its scale slider — the box (X/Y/W/H) is the model now.
  return key === 'rating' || key === 'deal' || key === 'price_card' || key === 'logo';
}

function hasShape(key: string, isCustom: boolean) {
  if (isCustom) return true;
  return key === 'rating' || key === 'deal' || key === 'price_card';
}

function hasFontFamily(key: string, isCustom: boolean) {
  if (isCustom) return true;
  // price_card is excluded: it has per-line family selectors instead.
  return key === 'rating' || key === 'deal' || key === 'disclaimer';
}

function hasShadow(key: string, isCustom: boolean) {
  if (isCustom) return true;
  return key === 'rating' || key === 'deal' || key === 'price_card' || key === 'logo';
}

function hasBorderRadius(key: string, isCustom: boolean) {
  if (isCustom) return true;
  return key === 'rating' || key === 'deal' || key === 'price_card';
}

function SliderField({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
  testId,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (v: number) => string;
  onChange: (v: number) => void;
  testId?: string;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="ff-label !mb-0">{label}</label>
        <span className="font-mono text-[11px] text-ff-mute tabular-nums">{format(value)}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="ff-slider w-full accent-ff-forest"
        data-testid={testId}
      />
    </div>
  );
}

type ShapeValue = 'pill' | 'circle' | 'rounded-square' | 'none';

const SHAPES: Array<{ value: ShapeValue; label: string }> = [
  { value: 'pill', label: 'Pill' },
  { value: 'rounded-square', label: 'Square' },
  { value: 'circle', label: 'Circle' },
  { value: 'none', label: 'None' },
];

function ShapeSelector({
  elKey,
  value,
  onChange,
}: {
  elKey: string;
  value: ShapeValue;
  onChange: (v: ShapeValue) => void;
}) {
  return (
    <div>
      <label className="ff-label">Shape</label>
      <div
        className="grid grid-cols-4 gap-1 rounded-xl border border-ff-sand/60 bg-white/40 p-1"
        role="tablist"
        data-testid={`element-${elKey}-shape`}
      >
        {SHAPES.map((s) => {
          const active = value === s.value;
          return (
            <button
              key={s.value}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => onChange(s.value)}
              className={`text-[11px] font-mono uppercase tracking-widish py-1.5 rounded-lg transition-colors ${
                active
                  ? 'bg-ff-forest text-ff-ivory shadow-soft'
                  : 'text-ff-mute hover:text-ff-forest'
              }`}
              data-testid={`element-${elKey}-shape-${s.value}`}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function FontFamilySelector({
  elKey,
  value,
  onChange,
}: {
  elKey: string;
  value: FontFamily;
  onChange: (v: FontFamily) => void;
}) {
  return (
    <div>
      <label className="ff-label">Font family</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as FontFamily)}
        className="ff-input text-sm"
        data-testid={`element-${elKey}-font-family`}
      >
        {FONT_FAMILIES.map((f) => (
          <option key={f} value={f}>
            {FONT_FAMILY_LABEL[f]}
          </option>
        ))}
      </select>
    </div>
  );
}

function ShadowControl({
  elKey,
  shadow,
  onChange,
}: {
  elKey: string;
  shadow: ShadowSpec | null;
  onChange: (s: ShadowSpec | null) => void;
}) {
  const enabled = shadow !== null && shadow !== undefined;
  const cur = shadow ?? { blur: 12, opacity: 0.35, dx: 0, dy: 4 };
  return (
    <div className="rounded-xl border border-ff-sand/60 bg-white/40 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <label className="ff-label !mb-0">Drop shadow</label>
        <button
          type="button"
          onClick={() => onChange(enabled ? null : cur)}
          className={`text-[10px] font-mono uppercase tracking-widish px-2 py-0.5 rounded-md transition-colors ${
            enabled ? 'bg-ff-forest text-ff-ivory' : 'bg-ff-sand/40 text-ff-mute'
          }`}
          data-testid={`element-${elKey}-shadow-toggle`}
        >
          {enabled ? 'On' : 'Off'}
        </button>
      </div>
      {enabled && (
        <>
          <SliderField
            label="Blur"
            value={cur.blur}
            min={0}
            max={48}
            step={1}
            format={(v) => `${Math.round(v)} px`}
            onChange={(v) => onChange({ ...cur, blur: Math.round(v) })}
            testId={`element-${elKey}-shadow-blur`}
          />
          <SliderField
            label="Opacity"
            value={cur.opacity}
            min={0}
            max={1}
            step={0.05}
            format={(v) => v.toFixed(2)}
            onChange={(v) => onChange({ ...cur, opacity: round2(v) })}
            testId={`element-${elKey}-shadow-opacity`}
          />
          <div className="grid grid-cols-2 gap-3">
            <NumField
              label="dX"
              value={cur.dx}
              onChange={(v) => onChange({ ...cur, dx: v })}
            />
            <NumField
              label="dY"
              value={cur.dy}
              onChange={(v) => onChange({ ...cur, dy: v })}
            />
          </div>
        </>
      )}
    </div>
  );
}

function hasFill(el: Record<string, unknown>) {
  return typeof el.fill === 'string';
}
function hasTextColor(el: Record<string, unknown>) {
  return typeof el.text_color === 'string';
}
function hasSaleColor(el: Record<string, unknown>) {
  return typeof el.sale_color === 'string';
}
function hasColor(el: Record<string, unknown>) {
  return typeof el.color === 'string';
}

function NumField({
  label,
  value,
  onChange,
  testId,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  testId?: string;
}) {
  return (
    <div>
      <label className="ff-label">{label}</label>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="ff-input font-mono text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function AddOverlayDialog({
  existingKeys,
  sourceFields,
  onCancel,
  onCreate,
}: {
  existingKeys: string[];
  sourceFields: SourceFields;
  onCancel: () => void;
  onCreate: (key: string, element: Record<string, unknown>) => void;
}) {
  const [type, setType] = useState<'text' | 'field'>('text');
  const [value, setValue] = useState('EXCLUSIVE');
  const [field, setField] = useState(
    sourceFields.from_source[0] ?? sourceFields.custom[0] ?? 'brand',
  );

  function nextKey(prefix: string): string {
    let i = 1;
    while (existingKeys.includes(`${prefix}_${i}`)) i++;
    return `${prefix}_${i}`;
  }

  function submit() {
    if (type === 'text') {
      const text = value.trim() || 'New tag';
      onCreate(nextKey('custom'), {
        type: 'text',
        value: text,
        x: 540,
        y: 200,
        fill: '#0B3D2E',
        text_color: '#FFFFFF',
        font_family: 'Inter',
        font_size: 32,
        shape: 'rounded-square',
        visible: true,
      });
    } else {
      onCreate(nextKey('custom'), {
        type: 'field',
        field,
        x: 540,
        y: 200,
        fill: '#E0382B',
        text_color: '#FFFFFF',
        font_family: 'Inter',
        font_size: 28,
        shape: 'pill',
        visible: true,
      });
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ff-forest/30 backdrop-blur-sm animate-rise"
      role="dialog"
      aria-modal="true"
      data-testid="add-overlay-dialog"
    >
      <div className="ff-card w-full max-w-md p-6 space-y-5">
        <header>
          <p className="font-mono text-[11px] uppercase tracking-widish text-ff-mute">
            Composition
          </p>
          <h2 className="font-display text-2xl text-ff-forest">Add overlay</h2>
          <p className="text-sm text-ff-mute mt-1">
            A pinned text label or a value pulled from the feed.
          </p>
        </header>

        <div>
          <label className="ff-label">Kind</label>
          <div className="grid grid-cols-2 gap-1 rounded-xl border border-ff-sand/60 bg-white/40 p-1">
            {(['text', 'field'] as const).map((t) => {
              const active = type === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`text-[11px] font-mono uppercase tracking-widish py-1.5 rounded-lg transition-colors ${
                    active
                      ? 'bg-ff-forest text-ff-ivory shadow-soft'
                      : 'text-ff-mute hover:text-ff-forest'
                  }`}
                  data-testid={`add-overlay-kind-${t}`}
                >
                  {t === 'text' ? 'Text' : 'Source field'}
                </button>
              );
            })}
          </div>
        </div>

        {type === 'text' ? (
          <div>
            <label className="ff-label">Text</label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="ff-input font-mono text-sm"
              placeholder="EXCLUSIVE"
              data-testid="add-overlay-text"
              autoFocus
            />
          </div>
        ) : (
          <div>
            <label className="ff-label">Field</label>
            <select
              value={field}
              onChange={(e) => setField(e.target.value)}
              className="ff-input text-sm"
              data-testid="add-overlay-field"
            >
              {sourceFields.from_source.length > 0 && (
                <optgroup label="From the source">
                  {sourceFields.from_source.map((f) => (
                    <option key={`src-${f}`} value={f}>{f}</option>
                  ))}
                </optgroup>
              )}
              {sourceFields.custom.length > 0 && (
                <optgroup label="Custom labels">
                  {sourceFields.custom.map((f) => (
                    <option key={`cus-${f}`} value={f}>{f}</option>
                  ))}
                </optgroup>
              )}
            </select>
            <p className="mt-1.5 text-[11px] text-ff-mute">
              Renders as a pill containing the product's value for this field.
            </p>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} className="ff-btn-ghost">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className="ff-btn-primary"
            data-testid="add-overlay-submit"
          >
            Add overlay
          </button>
        </div>
      </div>
    </div>
  );
}

function AssetsBlock({
  config,
  onUpload,
  uploading,
}: {
  config: DesignConfig;
  onUpload: (kind: 'background' | 'logo' | 'lifestyle', file: File) => Promise<void>;
  uploading: 'background' | 'logo' | 'lifestyle' | null;
}) {
  return (
    <section>
      <p className="font-mono text-[11px] uppercase tracking-widish text-ff-mute mb-3">
        Assets
      </p>
      <div className="space-y-2">
        <UploadField
          label="Background"
          path={config.assets.background}
          loading={uploading === 'background'}
          onPick={(f) => onUpload('background', f)}
          accept="image/jpeg,image/png"
        />
        <UploadField
          label="Logo"
          path={config.assets.logo}
          loading={uploading === 'logo'}
          onPick={(f) => onUpload('logo', f)}
          accept="image/png,image/jpeg"
        />
      </div>
    </section>
  );
}

function UploadField({
  label,
  path,
  loading,
  onPick,
  accept,
}: {
  label: string;
  path: string;
  loading: boolean;
  onPick: (f: File) => void;
  accept: string;
}) {
  return (
    <label className="flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border border-ff-sand/60 bg-white/40 hover:border-ff-forest/30 cursor-pointer transition-colors">
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-mono uppercase tracking-widish text-ff-mute">
          {label}
        </span>
        <span className="block text-xs font-mono text-ff-ink/70 truncate">
          {path.split('/').pop()}
        </span>
      </span>
      <span className="ff-chip bg-ff-mint text-ff-forest">
        {loading ? 'Uploading…' : 'Replace'}
      </span>
      <input
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onPick(f);
          e.target.value = '';
        }}
      />
    </label>
  );
}
